import { Controller } from "@hotwired/stimulus"
import { scrollContainerFor } from "../utils/scroll_container"

export default class extends Controller {
  static targets = ["sidebar", "tocNav", "toggleBtn", "article", "backToTop", "floatingToggle"]

  connect() {
    this.setupScrollListener()
    this.setupResizeListener()
    this.scrollTocToActive()
  }

  disconnect() {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler, true)
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
    }
  }

  setupScrollListener() {
    this.scrollHandler = this.throttle(() => {
      this.updateBackToTopVisibility()
    }, 16) // ~60fps

    // Capture phase: in the admin area the page scroll belongs to an element, not the document.
    window.addEventListener('scroll', this.scrollHandler, true)
  }

  setupResizeListener() {
    this.resizeHandler = this.debounce(() => {
      this.checkMobileView()
    }, 100)

    window.addEventListener('resize', this.resizeHandler)
    this.checkMobileView()
  }

  scrollTocToActive() {
    if (!this.hasTocNavTarget) return
    
    const activeLink = this.tocNavTarget.querySelector('.toc-link.active')
    if (activeLink) {
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        activeLink.scrollIntoView({ behavior: 'instant', block: 'center' })
      }, 100)
    }
  }

  scrollToTop() {
    scrollContainerFor(this.element).scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }

  updateBackToTopVisibility() {
    if (!this.hasBackToTopTarget) return

    const scrollTop = scrollContainerFor(this.element).scrollTop
    const showThreshold = 400

    if (scrollTop > showThreshold) {
      this.backToTopTarget.classList.add('visible')
    } else {
      this.backToTopTarget.classList.remove('visible')
    }
  }

  toggleToc() {
    if (this.hasSidebarTarget) {
      const isCollapsed = this.sidebarTarget.classList.toggle('collapsed')
      this.updateFloatingToggleVisibility(isCollapsed)
    }
  }

  openToc() {
    if (this.hasSidebarTarget) {
      this.sidebarTarget.classList.remove('collapsed')
      
      // Handle mobile
      if (window.innerWidth <= 1024) {
        this.sidebarTarget.classList.add('open')
      }
      
      // Hide floating toggle when TOC is open
      this.updateFloatingToggleVisibility(false)
    }
  }

  updateFloatingToggleVisibility(show) {
    if (this.hasFloatingToggleTarget) {
      if (show) {
        this.floatingToggleTarget.classList.add('visible')
      } else {
        this.floatingToggleTarget.classList.remove('visible')
      }
    }
  }

  checkMobileView() {
    if (window.innerWidth <= 1024) {
      this.setupMobileToggle()
    } else {
      this.removeMobileToggle()
    }
  }

  setupMobileToggle() {
    if (this.hasSidebarTarget) {
      this.sidebarTarget.classList.remove('collapsed')
      this.sidebarTarget.classList.remove('open')
    }
    // Show floating toggle on mobile since sidebar is hidden
    this.updateFloatingToggleVisibility(true)
  }

  removeMobileToggle() {
    if (this.hasSidebarTarget) {
      this.sidebarTarget.classList.remove('open')
      // Hide floating toggle on desktop unless sidebar is collapsed
      const isCollapsed = this.sidebarTarget.classList.contains('collapsed')
      this.updateFloatingToggleVisibility(isCollapsed)
    }
  }

  closeSidebar() {
    if (this.hasSidebarTarget) {
      this.sidebarTarget.classList.remove('open')
      // Show floating toggle again on mobile
      if (window.innerWidth <= 1024) {
        this.updateFloatingToggleVisibility(true)
      }
    }
  }

  // Utility functions
  throttle(func, limit) {
    let inThrottle
    return function(...args) {
      if (!inThrottle) {
        func.apply(this, args)
        inThrottle = true
        setTimeout(() => inThrottle = false, limit)
      }
    }
  }

  debounce(func, wait) {
    let timeout
    return function(...args) {
      clearTimeout(timeout)
      timeout = setTimeout(() => func.apply(this, args), wait)
    }
  }
}
